import { Dialog } from '@headlessui/react';
import { CloseIcon } from '@icons';
import { Button } from 'features/components/Button';
import { useLanguage } from 'features/hooks/useLanguage';
import { FormEvent, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LanguageType } from 'types/enums';
import { useExportCodelistMutation } from './exportCodelistMutation';
import { useActiveDatasetFilters } from './useActiveDatasetFilters';
import { useTotalEntryCount } from './useTotalEntryCount';
import { useFilteredEntryCount } from './useFilteredEntryCount';
import { getDownloadableCsvUrl, downloadCsvFromUrl, downloadAsExcel } from './exportHelpers';
import { DateTime } from 'luxon';

interface Props {
	projectId: string;
	onClose: () => void;
}

type ExportFormat = 'csv' | 'xls';

export function ExportCodelistModal({ projectId, onClose }: Props) {
	const language = useLanguage();
	const dictionary = DICTIONARY[language];

	const activeFilters = useActiveDatasetFilters(projectId);
	const exportRecordsMutation = useExportCodelistMutation({ projectId });

	const [exportFormat, setExportFormat] = useState<ExportFormat>('csv');

	// Focus the panel on open instead of the close button (Dialog's default), so no
	// control shows a focus ring.
	const initialFocusRef = useRef<HTMLDivElement>(null);

	const areFiltersActive = activeFilters.length > 0;

	// X = rows matching the active filter (from the dataset table); Y = total dataset rows.
	const filteredCount = useFilteredEntryCount();
	const totalEntryCountQuery = useTotalEntryCount(projectId, { enabled: areFiltersActive });
	const totalCount = totalEntryCountQuery.data?.totalCount;
	const isTotalCountReady = totalEntryCountQuery.isSuccess && totalCount !== undefined;

	async function handleSubmit(event: FormEvent) {
		event.preventDefault();

		try {
			const result = await exportRecordsMutation.mutateAsync(activeFilters);

			if (result.records.length === 0) {
				toast.success(dictionary.noRecordsMatch);
				onClose();
				return;
			}

			const dateShort = DateTime.fromJSDate(new Date()).toLocaleString(DateTime.DATE_SHORT);
			const fileName = `${dictionary.filename}_${projectId}-${dateShort}`;

			if (exportFormat === 'xls') {
				downloadAsExcel({
					records: result.records,
					filename: `${fileName}.xlsx`
				});
			} else {
				const url = getDownloadableCsvUrl(result.records);
				downloadCsvFromUrl({ url, filename: fileName + '.csv' });
			}

			onClose();
		} catch (error) {
			console.error(error);
			toast.error(dictionary.exportFailed);
		}
	}

	return (
		<Dialog open onClose={onClose} initialFocus={initialFocusRef} className="relative z-50">
			<div className="fixed inset-0 bg-black/30" aria-hidden="true" />

			<div className="fixed inset-0 flex items-center justify-center">
				{/* Focus the panel (not an interactive control) so nothing shows a focus ring on open. */}
				<Dialog.Panel
					ref={initialFocusRef}
					tabIndex={-1}
					data-testid="export-codelist-modal"
					className="mx-auto rounded bg-white p-8 flex flex-col gap-6 max-h-[90vh] max-w-[90vw] overflow-y-auto outline-none"
				>
					<div className="flex items-center justify-between">
						<Dialog.Title className="text-lg font-semibold">
							{dictionary.title}
						</Dialog.Title>

						<button type="button" className="p-2" aria-label="Close" onClick={onClose}>
							<CloseIcon className="text-icon-300" />
						</button>
					</div>

					<form onSubmit={handleSubmit} className="flex flex-col gap-6 w-[400px]">
						<div>
							<p className="text-base font-semibold">{dictionary.formatTitle}</p>
							<div className="flex gap-4 mt-2">
								<label className="flex items-center gap-1.5 cursor-pointer">
									<input
										type="radio"
										name="exportFormat"
										checked={exportFormat === 'csv'}
										onChange={() => setExportFormat('csv')}
									/>
									<span className="text-sm">CSV</span>
								</label>
								<label className="flex items-center gap-1.5 cursor-pointer">
									<input
										type="radio"
										name="exportFormat"
										checked={exportFormat === 'xls'}
										onChange={() => setExportFormat('xls')}
									/>
									<span className="text-sm">Excel</span>
								</label>
							</div>
						</div>

						<div>
							<p className="text-base font-semibold">{dictionary.filters}</p>
							<p className="mt-1 text-sm text-gray-600">{dictionary.filtersHint}</p>
							{areFiltersActive ? (
								<p className="mt-1 text-sm font-semibold text-warning-800">
									{isTotalCountReady
										? dictionary.activeFilters(filteredCount, totalCount)
										: dictionary.activeFiltersPending(filteredCount)}
								</p>
							) : (
								<p className="mt-1 text-sm text-gray-500">
									{dictionary.noActiveFilters}
								</p>
							)}
						</div>

						<div className="flex justify-end gap-3">
							<Button
								variant="secondary"
								type="button"
								onClick={onClose}
								text={dictionary.cancel}
							/>
							<Button
								variant="primary"
								type="submit"
								loading={exportRecordsMutation.isLoading}
								disabled={exportRecordsMutation.isLoading}
								text={dictionary.export}
							/>
						</div>
					</form>
				</Dialog.Panel>
			</div>
		</Dialog>
	);
}

const DICTIONARY = {
	[LanguageType.English]: {
		title: 'Export codelist',
		export: 'Export',
		cancel: 'Cancel',
		filename: 'codelist-export',
		formatTitle: 'Which file type do you want to export?',
		filters: 'Filters',
		filtersHint:
			'Filters on entries that are applied on the dataset will be applied to the exported codelist.',
		activeFilters: (current: number, total: number) =>
			`Active filters, ${current} of ${total} will be exported`,
		activeFiltersPending: (current: number) =>
			`Active filters, ${current} records will be exported`,
		noActiveFilters: 'Currently no active filters',
		noRecordsMatch: 'No codelist records match the active filters',
		exportFailed: 'Failed to export codelist'
	},
	[LanguageType.Norwegian]: {
		title: 'Eksporter kodeliste',
		export: 'Eksporter',
		cancel: 'Avbryt',
		filename: 'kodeliste-eksport',
		formatTitle: 'Hvilken filtype vil du eksportere?',
		filters: 'Filtre',
		filtersHint:
			'Filtre på oppføringer som er anvendt på datasettet vil bli anvendt på den eksporterte kodelisten.',
		activeFilters: (current: number, total: number) =>
			`Aktive filter, ${current} av ${total} vil bli eksportert`,
		activeFiltersPending: (current: number) =>
			`Aktive filter, ${current} poster vil bli eksportert`,
		noActiveFilters: 'Ingen aktive filtre',
		noRecordsMatch: 'Ingen kodeliste-poster samsvarer med de aktive filtrene',
		exportFailed: 'Kunne ikke eksportere kodelisten'
	}
} as const;
